import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Percent, Calendar, Users, Tag, TrendingDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStr = () => new Date().toISOString().substring(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().substring(0, 10);
};
const fmtDateBR = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};
const fmtDateTimeBR = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
};
const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

export const DiscountsReport = () => {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayStr());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (s, e) => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(
        `${API}/sales/discount-report?start_date=${s}&end_date=${e}`,
        { withCredentials: true }
      );
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(start, end); /* eslint-disable-next-line */ }, []);

  const applyPreset = (preset) => {
    const t = todayStr();
    if (preset === 'today') { setStart(t); setEnd(t); load(t, t); }
    else if (preset === 'month') { const s = firstOfMonth(); setStart(s); setEnd(t); load(s, t); }
  };

  const search = () => load(start, end);

  return (
    <div data-testid="discounts-report">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Percent size={22} className="text-yellow-600" /> Dashboard de Descontos
          </h2>
          <p className="text-sm text-slate-500 mt-1">Vendas com desconto — vinculadas ao turno e operador</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600 mb-2 block flex items-center gap-1">
          <Calendar size={12} /> Período
        </Label>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button size="sm" variant="outline" onClick={() => applyPreset('today')} data-testid="disc-preset-today">Hoje</Button>
          <Button size="sm" variant="outline" onClick={() => applyPreset('month')} data-testid="disc-preset-month">Mês Atual</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500">De</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="disc-date-start"/>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Até</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="disc-date-end"/>
          </div>
          <div className="flex items-end">
            <Button onClick={search} disabled={loading} className="w-full bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="disc-search-btn">
              {loading ? 'Carregando...' : 'Aplicar Filtro'}
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>
      )}

      {report && (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <Card
              testid="disc-total"
              icon={TrendingDown}
              label="Total Descontado"
              value={money(report.total_discount)}
              color="text-yellow-600"
              bg="bg-yellow-50 border-yellow-200"
            />
            <Card
              testid="disc-count"
              icon={Tag}
              label="Vendas c/ Desconto"
              value={report.sales_count}
              color="text-blue-600"
              bg="bg-blue-50 border-blue-200"
            />
            <Card
              testid="disc-avg"
              icon={Percent}
              label="Desconto Médio"
              value={money(report.avg_discount)}
              color="text-purple-600"
              bg="bg-purple-50 border-purple-200"
            />
            <Card
              testid="disc-pct"
              icon={Percent}
              label="% da Receita Bruta"
              value={`${report.percent_of_revenue.toFixed(2)}%`}
              color="text-emerald-600"
              bg="bg-emerald-50 border-emerald-200"
            />
          </div>

          {/* Comparativo Bruto vs Líquido */}
          <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <SmallStat label="Receita Bruta (antes descontos)" value={money(report.revenue_before_discount)} />
            <SmallStat label="Total de Descontos" value={money(report.total_discount)} negative />
            <SmallStat label="Receita Líquida" value={money(report.revenue_after_discount)} strong />
          </div>

          {/* Grid 2 colunas: por operador e por justificativa */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <BreakdownTable
              testid="disc-by-operator"
              title="Descontos por Operador"
              icon={Users}
              rows={report.by_operator}
              columns={[
                { key: 'name', label: 'Operador', className: 'font-medium' },
                { key: 'count', label: 'Vendas', className: 'text-center' },
                { key: 'total_discount', label: 'Total', className: 'text-right font-mono font-bold', formatter: money },
              ]}
            />
            <BreakdownTable
              testid="disc-by-reason"
              title="Descontos por Justificativa"
              icon={Tag}
              rows={report.by_reason}
              columns={[
                { key: 'reason', label: 'Motivo', className: 'font-medium' },
                { key: 'count', label: 'Vendas', className: 'text-center' },
                { key: 'total_discount', label: 'Total', className: 'text-right font-mono font-bold', formatter: money },
              ]}
            />
          </div>

          {/* Detalhamento — lista de vendas com desconto */}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="bg-slate-100 border-b border-slate-200 px-4 py-2">
              <h3 className="font-bold text-slate-900 text-sm">
                Detalhamento das vendas com desconto ({report.sales.length})
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="disc-sales-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Nº</th>
                    <th className="text-left px-3 py-2">Data/Hora</th>
                    <th className="text-right px-3 py-2">Original</th>
                    <th className="text-right px-3 py-2">Desconto</th>
                    <th className="text-right px-3 py-2">Final</th>
                    <th className="text-left px-3 py-2">Operador</th>
                    <th className="text-left px-3 py-2">Justificativa</th>
                  </tr>
                </thead>
                <tbody>
                  {report.sales.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-6 text-slate-500">
                        Nenhuma venda com desconto no período.
                      </td>
                    </tr>
                  )}
                  {report.sales.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`disc-sale-${s.sale_number}`}>
                      <td className="px-3 py-2 font-mono font-bold">#{s.sale_number}</td>
                      <td className="px-3 py-2 text-xs">{fmtDateTimeBR(s.created_at)}</td>
                      <td className="px-3 py-2 text-right font-mono">{money(s.original_value)}</td>
                      <td className="px-3 py-2 text-right font-mono text-yellow-700 font-bold">- {money(s.discount)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{money(s.final_value)}</td>
                      <td className="px-3 py-2 text-xs">{s.operator_name}</td>
                      <td className="px-3 py-2 text-xs italic">{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Card = ({ icon: Icon, label, value, color, bg, testid }) => (
  <div className={`border rounded-md p-4 ${bg}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
      <Icon size={16} className={color}/>
    </div>
    <p className={`text-2xl font-black ${color}`}>{value}</p>
  </div>
);

const SmallStat = ({ label, value, negative, strong }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
    <p className={`text-xl font-bold mt-0.5 ${negative ? 'text-yellow-700' : strong ? 'text-emerald-700' : 'text-slate-800'}`}>
      {negative && '- '}{value}
    </p>
  </div>
);

const BreakdownTable = ({ title, icon: Icon, rows, columns, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
    <div className="bg-slate-100 border-b border-slate-200 px-4 py-2 flex items-center gap-2">
      <Icon size={14} className="text-slate-600"/>
      <h3 className="font-bold text-slate-900 text-sm">{title}</h3>
    </div>
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
        <tr>
          {columns.map(c => (
            <th key={c.key} className={`px-3 py-2 ${c.className || 'text-left'}`}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(!rows || rows.length === 0) && (
          <tr><td colSpan={columns.length} className="text-center py-4 text-slate-500 text-xs">Sem dados no período</td></tr>
        )}
        {rows && rows.map((row, i) => (
          <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
            {columns.map(c => (
              <td key={c.key} className={`px-3 py-2 ${c.className || ''}`}>
                {c.formatter ? c.formatter(row[c.key]) : row[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
